import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import TournamentResultsView from "./TournamentResultsView";
import { cleanTournamentName } from "../utils/cleanTournamentName";
import { leagueRoundPathFor, roundPathFor } from "./tournament-round-redirect";

interface TournamentMeta {
  name?: string | null;
  date?: string | null;
  source?: string | null;
  leagueId?: number | null;
  leagueRoundId?: number | null;
  leagueClubPublicListed?: boolean | null;
}

function formatTournamentDate(raw: string | null | undefined): string {
  if (!raw) {
    return "";
  }
  const d = new Date(raw);

  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const TournamentDetails: React.FC = () => {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const [meta, setMeta] = useState<TournamentMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    fetch(`/api/public/tournaments/${tournamentId}`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((data: TournamentMeta) => {
        if (cancelled) {
          return;
        }
        setMeta(data);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) {
          return;
        }
        setNotFound(true);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <div>Wczytywanie turnieju…</div>
        </div>
      </div>
    );
  }

  const roundPath = roundPathFor(meta);

  if (roundPath) {
    return <Navigate to={roundPath} replace />;
  }

  if (notFound || !tournamentId) {
    return (
      <div className="card">
        <div style={{ textAlign: "center", padding: "2rem" }}>
          <h2>Nie znaleziono turnieju</h2>
          <p>Nie udało się znaleźć turnieju #{tournamentId}</p>
          <Link to="/tournaments" className="player-link">
            ← Powrót do listy turniejów
          </Link>
        </div>
      </div>
    );
  }

  const tournamentNumber = parseInt(tournamentId, 10);
  const displayedName =
    cleanTournamentName(meta?.name ?? "") || `Turniej ${tournamentNumber}`;
  const dateLabel = formatTournamentDate(meta?.date);
  const leagueId = meta?.leagueId ?? null;
  const leagueRoundId = meta?.leagueRoundId ?? null;
  const roundHref = leagueRoundPathFor(meta);
  const leagueLinksAllowed = meta?.leagueClubPublicListed === true;

  return (
    <div>
      <div className="card">
        <div style={{ marginBottom: "1rem" }}>
          <h2 style={{ margin: 0 }}>{displayedName}</h2>
          <p
            style={{
              margin: "0.4rem 0 0 0",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            {dateLabel ? <span>{dateLabel} · </span> : null}
            <span>Turniej #{tournamentNumber}</span>
          </p>
          {leagueLinksAllowed && leagueId && (
            <p style={{ margin: "0.75rem 0 0 0" }}>
              <Link to={`/leagues/${leagueId}`} className="player-link">
                Idź do ligi →
              </Link>
              {roundHref && (
                <>
                  {" · "}
                  <Link to={roundHref} className="player-link">
                    Szczegóły kolejki →
                  </Link>
                </>
              )}
            </p>
          )}
        </div>
        <div style={{ textAlign: "right" }}>
          <Link to="/tournaments" className="player-link">
            ← Powrót do listy turniejów
          </Link>
        </div>
      </div>

      <TournamentResultsView
        tournamentNumber={tournamentNumber}
        leagueRoundId={leagueLinksAllowed ? leagueRoundId ?? undefined : undefined}
        showGroupStandings={leagueRoundId !== null}
      />
    </div>
  );
};

export default TournamentDetails;
